import { color, flexbox, layout, space, typography } from "styled-system";
import solasColors from "@boi/design-tokens/dist/web/boi/light/tokens.json";
import { media, remCalc } from "../../../theme/style-utils-coras-nua";

import defaultTheme from "../../../theme/theme-test-override";
import styled from "styled-components";

const LabelContainer = styled("div")`
    ${layout}
    ${flexbox}
    ${space}
    order: 1;
    .field-label-hide & {
        display: none;
    }
    .field-label {
        color: ${props => props.theme.colors.monotoneDark};
        font-size: ${remCalc(16)};
        line-height: ${remCalc(24)};
        font-weight: ${props => props.theme.fontWeights.semiBold};
        display: inline;
        margin-left: 0;
        padding-left: 0;
        &.field-sub-label {
            color: ${props => props.theme.colors.monotoneDark};
            font-size: ${remCalc(14)};
            line-height: ${remCalc(24)};
            font-weight: ${props => props.theme.fontWeights.regular};
            display: block;
            margin-top: ${remCalc(-4)};
            margin-bottom: ${remCalc(0)};
        }
    }
    .field-sub-label {
        color: ${props => props.theme.colors.monotoneDark};
        font-size: ${remCalc(14)};
        line-height: ${remCalc(24)};
        font-weight: ${props => props.theme.fontWeights.regular};
        display: block;
        margin-top: ${remCalc(-4)};
        margin-bottom: ${remCalc(0)};
    }
    .remove-optional {
        .optional {
            display: none;
        }
    }
`;
LabelContainer.defaultProps = {
    theme: {
        ...defaultTheme
    },
    display: "inline",
    pb: remCalc(8)
};

const FieldDescription = styled("div")`
    ${color}
    ${typography}
    ${space}
    * {
        font-size: ${remCalc(14)};
        &:last-child {
            margin-bottom: 0;
        }
    }
    ul{
        margin-top: 0;
        margin-left: ${remCalc(32)};
        li{
            padding-top: 0;
        }
    }
    .u-list-indented &{
        ul{
            margin-left: ${props => props.theme.space[3]};
        }
    }
    &.field-description-above {
        order: 2;
    }
    &.field-description-below {
        margin-top: ${remCalc(10)};
        margin-bottom: 0;
        order: 4;
    }
`;
FieldDescription.defaultProps = {
    theme: {
        ...defaultTheme
    },
    color: "monotoneDark",
    fontSize: 1,
    mb: 2,
    mt: -1
};

const FieldError = styled("div")`
    ${typography}
    ${color}
    ${space}
    order: 5;
`;
FieldError.defaultProps = {
    theme: {
        ...defaultTheme
    },
    fontSize: 2,
    mt: 1,
    color: "alert"
};

const StyledContainer = styled.div`
    /* 1. General Styles */
    ${space}
    ${layout}
    ${flexbox}
    ${color}
    visibility: visible;
    transition: opacity ease-in-out 500ms;
    ${"" /* IE11, Win 7 needs min-height set so images resize as expected. */}
    min-height: ${remCalc(1)};
    > fieldset{
        border: 0;
        padding: 0;
        margin: 0;
        display: flex;
        flex-direction: column;
        order: 1;
    }
    > div{
        min-height: ${remCalc(1)};
    }
    strong{
        font-weight: ${props => props.theme.fontWeights.semiBold};
    }
    h3{
        font-size: ${remCalc(18)};
        line-height: ${props => props.theme.lineHeights[6]};
        color: ${props => props.theme.colors.monotoneDark};
        font-weight: ${props => props.theme.fontWeights.bold};
        margin-top: 0;
        margin-bottom: ${props => props.theme.space[2]};
    }
    > p:last-child {
        margin-bottom: 0;
    }
    /* 2. Styles for classes added via Gravity admin */
    &.disabled,
    &.remove-optional {
        .optional {
            display: none;
        }
    }
    &.disabled,
    &.readonly,
    &.disabled.hide,
    &.readonly.hide {
        transition: none;
    }
    &.hide {
        visibility: hidden;
        opacity: 0;
        position: fixed;
        width: ${remCalc(1)};
        height: ${remCalc(1)};
    }
    &.description-size-normal{
        ${FieldDescription} *{
            font-size: ${remCalc(16)};
        }
    }
    &.description-reduced-spacing{
        ${FieldDescription}{
            margin-top: 0;
            ul{
                margin-bottom: 0;
                padding-top: 0;
                padding-bottom: 0;
            }
        }
    }
    &.description-p-indented{
        p{
            margin-left: ${remCalc(36)};
        }
    }
    &.checkbox-link, &.radio-link, &.checkbox-radio-link {
        .checkbox, .radio {
            >div{
                width: 100%;
                .field-label {
                    padding-left: ${remCalc(0)};
                    background: none;
                    color: inherit;
                    border: none;
                    text-shadow: none;
                    justify-content: flex-start;
                    padding: 0;
                    margin-bottom: ${remCalc(14)};
                    > div{
                        font-weight: ${props =>
                            props.theme.fontWeights.semiBold};
                        text-decoration: underline;
                        justify-content: flex-start;
                        cursor: pointer;
                    }
                    &:hover {
                        > div{
                            color: ${props => props.theme.colors.primary};
                        }
                    }
                    &:before {
                        content: none;
                    }
                }
                input:focus {
                    & + .field-label {
                        outline: -moz-mac-focusring solid ${remCalc(2)};
                        @media (-webkit-min-device-pixel-ratio: 0) {
                            outline: -webkit-focus-ring-color auto ${remCalc(
                                5
                            )};
                        }
                    }
                }
            }
        }
    }
    &.accordion-chevron{
        .checkbox, .radio{
            > div{
                width: 100%;
                .field-label{
                    display: flex;
                    > div{
                        order: 1;
                        padding-right: ${props => props.theme.space[5]};
                        content: ' ';
                        height: auto;
                        min-height: ${props => props.theme.space[5]};
                        background-size: ${remCalc(16)} ${remCalc(16)};
                        background-repeat: no-repeat;
                        background-position: right center;
                    }
                }
                input + .field-label {
                    > div{
                        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' fill-rule='evenodd' xmlns:v='https://vecta.io/nano'%3E%3Cpath fill='none' d='M-826-1513H454v6951H-826z'/%3E%3Cpath d='M3.825 7.585a.5.5 0 0 1 .547.131l7.63 8.58 7.63-8.58a.5.5 0 0 1 .894.146.5.5 0 0 1-.154.514l-8 9a.49.49 0 0 1-.74 0l-8-9a.46.46 0 0 1-.13-.33.5.5 0 0 1 .323-.461z' fill='%23161616' fill-rule='nonzero'/%3E%3C/svg%3E");
                    }
                }
                input:checked + .field-label {
                    > div{
                        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' fill-rule='evenodd' xmlns:v='https://vecta.io/nano'%3E%3Cpath fill='none' d='M-826-1471H454v6951H-826z'/%3E%3Cpath d='M20.5 16.954a.5.5 0 0 1-.868.328l-7.611-8.54-7.611 8.54a.5.5 0 0 1-.892-.145c-.053-.186.007-.386.154-.512l7.98-8.958a.49.49 0 0 1 .738 0l7.98 8.958a.46.46 0 0 1 .13.328h0z' fill='%23161616' fill-rule='nonzero'/%3E%3C/svg%3E");
                    }
                }
                &:hover{
                    input + .field-label {
                        > div{
                            background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' fill-rule='evenodd' xmlns:v='https://vecta.io/nano'%3E%3Cpath fill='none' d='M-826-1513H454v6951H-826z'/%3E%3Cpath d='M3.825 7.585a.5.5 0 0 1 .547.131l7.63 8.58 7.63-8.58a.5.5 0 0 1 .894.146.5.5 0 0 1-.154.514l-8 9a.49.49 0 0 1-.74 0l-8-9a.46.46 0 0 1-.13-.33.5.5 0 0 1 .323-.461z' fill='%230000ff' fill-rule='nonzero'/%3E%3C/svg%3E");
                        }
                    }
                    input:checked + .field-label {
                        > div{
                            background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' fill-rule='evenodd' xmlns:v='https://vecta.io/nano'%3E%3Cpath fill='none' d='M-826-1471H454v6951H-826z'/%3E%3Cpath d='M20.5 16.954a.5.5 0 0 1-.868.328l-7.611-8.54-7.611 8.54a.5.5 0 0 1-.892-.145c-.053-.186.007-.386.154-.512l7.98-8.958a.49.49 0 0 1 .738 0l7.98 8.958a.46.46 0 0 1 .13.328h0z' fill='%230000ff' fill-rule='nonzero'/%3E%3C/svg%3E");
                        }
                    }
                }
            }
        }
    }
    &.accordion-contained-trigger {
        > div {
            > .field-label{
                display: none;
            }
        }

        &.dark {
            .checkbox {
                background-color: ${props =>
                    props.theme.colors.monotoneMidLight};

                .field-label{
                    border-color: ${props =>
                        props.theme.colors.monotoneMidLight};
                }
            }
        }

        .checkbox {
            .field-label {
                height: ${remCalc(64)};
                padding: 0 ${remCalc(16)};
                margin-bottom: 0;
                border: ${remCalc(1)} solid ${props =>
    props.theme.colors.backgroundLighter};
                border-radius: ${remCalc(4)};
                font-weight: ${props => props.theme.fontWeights.bold};
                cursor: pointer;
                display: flex;
                justify-content: space-between;
                align-items: center;
                > div{
                    color: ${props => props.theme.colors.monotoneDark};
                }
                &:before {
                    content: none;
                }
                &:after{
                    width: ${remCalc(24)};
                    height: ${remCalc(24)};
                    margin-left: ${props => props.theme.space[2]};
                    color: ${props => props.theme.colors.primary};
                    content: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' fill-rule='evenodd' xmlns:v='https://vecta.io/nano'%3E%3Cpath fill='none' d='M-826-1513H454v6951H-826z'/%3E%3Cpath d='M3.825 7.585a.5.5 0 0 1 .547.131l7.63 8.58 7.63-8.58a.5.5 0 0 1 .894.146.5.5 0 0 1-.154.514l-8 9a.49.49 0 0 1-.74 0l-8-9a.46.46 0 0 1-.13-.33.5.5 0 0 1 .323-.461z' fill='%23161616' fill-rule='nonzero'/%3E%3C/svg%3E");
                }
            }
            input:checked + .field-label {
                border-radius: ${remCalc(5)} ${remCalc(5)} 0 0;
                border-bottom: ${remCalc(1)} solid ${props =>
    props.theme.colors.monotoneMidDark};
                &:after{
                    content: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' fill-rule='evenodd' xmlns:v='https://vecta.io/nano'%3E%3Cpath fill='none' d='M-826-1471H454v6951H-826z'/%3E%3Cpath d='M20.5 16.954a.5.5 0 0 1-.868.328l-7.611-8.54-7.611 8.54a.5.5 0 0 1-.892-.145c-.053-.186.007-.386.154-.512l7.98-8.958a.49.49 0 0 1 .738 0l7.98 8.958a.46.46 0 0 1 .13.328h0z' fill='%23161616' fill-rule='nonzero'/%3E%3C/svg%3E");
                }
            }
        }
    }
    &.accordion-contained-content{
        border: ${remCalc(1)} solid ${props =>
    props.theme.colors.backgroundLighter};
        border-top: 0;
        border-radius: 0 0 ${remCalc(4)} ${remCalc(4)};
        padding: ${remCalc(24)} ${remCalc(16)};
        margin-top: -${remCalc(32)};
        margin-bottom: ${remCalc(32)};
        h1, h2, h3, h4, h5, h6{
            margin-bottom: ${remCalc(24)}
        }
        hr{
            border-color: ${props => props.theme.colors.monotoneMidDark};
        }

        &.dark{
            background-color: ${props => props.theme.colors.monotoneMidLight};
            border-color: ${props => props.theme.colors.monotoneMidLight};
        }
    }
    &.info-message-trigger {
        input:checked + label {
            &:after {
                z-index: 3;
                position: fixed;
                bottom: 0;
                left: 0;
                background-color: ${props => props.theme.colors.brilliant};
                background-image: url("data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyB3aWR0aD0iMjBweCIgaGVpZ2h0PSIxNXB4IiB2aWV3Qm94PSIwIDAgMjAgMTUiIHZlcnNpb249IjEuMSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB4bWxuczp4bGluaz0iaHR0cDovL3d3dy53My5vcmcvMTk5OS94bGluayI+CiAgICA8IS0tIEdlbmVyYXRvcjogU2tldGNoIDUzLjEgKDcyNjMxKSAtIGh0dHBzOi8vc2tldGNoYXBwLmNvbSAtLT4KICAgIDx0aXRsZT5sPC90aXRsZT4KICAgIDxkZXNjPkNyZWF0ZWQgd2l0aCBTa2V0Y2guPC9kZXNjPgogICAgPGcgaWQ9IlBhZ2UtMSIgc3Ryb2tlPSJub25lIiBzdHJva2Utd2lkdGg9IjEiIGZpbGw9Im5vbmUiIGZpbGwtcnVsZT0iZXZlbm9kZCI+CiAgICAgICAgPGcgaWQ9IkFydGJvYXJkIiBmaWxsPSIjMTA2OTg4IiBmaWxsLXJ1bGU9Im5vbnplcm8iPgogICAgICAgICAgICA8cGF0aCBkPSJNOC4wMjU1MjA1MiwxNC43OTM5OTY2IEM3Ljc1MDk3NzQ3LDE1LjA2ODU5NjkgNy4zMDU3NDYyMywxNS4wNjg1OTY5IDcuMDMxMjAzMTgsMTQuNzk0MDUzOSBMMC4yMDU5MDcyODgsNy45Mzg1NzgyOCBDLTAuMDY4NjM1NzYyOCw3LjY2MzkyMDc5IC0wLjA2ODYzNTc2MjgsNy4zMzU5Mjk1IDAuMjA1OTA3Mjg4LDcuMDYxMzI5MjMgTDcuMDMxMjAzMTgsMC4yMDU5MDcyODggQzcuMzA1NzQ2MjMsLTAuMDY4NjM1NzYyOCA3Ljc1MDk3NzQ3LC0wLjA2ODYzNTc2MjggOC4wMjU1MjA1MiwwLjIwNTkwNzI4OCBMOC40Mzk4NTI3OSwwLjYyMDE4MjMzOSBDOC43MTQzOTU4NCwwLjg5NDgzOTgzIDguNzE0Mzk1ODQsMS4zNDAwMTM4NSA4LjQzOTg1Mjc5LDEuNjE0NTU2OSBMMy41MjIyMjQ4NCw2LjU2MjQyNTM0IEwxOS4yOTk5Njk1LDYuNTYyNDI1MzQgQzE5LjY4ODI2NjUsNi41NjI0MjUzNCAxOS45OTk5Njk1LDYuODE4Njg2OTUgMTkuOTk5OTY5NSw3LjIwNjk4Mzk3IEwxOS45OTk5Njk1LDcuNzkyOTE5OTYgQzE5Ljk5OTk2OTUsOC4xODEyMTY5OSAxOS42ODgyNjY1LDguNDM3NDI0OTUgMTkuMjk5OTY5NSw4LjQzNzQyNDk1IEwzLjUyMjIyNDg0LDguNDM3NDI0OTUgTDguNDM5ODUyNzksMTMuMzg1MzQ3IEM4LjcxNDM5NTg0LDEzLjY1OTk0NzMgOC43MTQzOTU4NCwxNC4xMDUxMjEzIDguNDM5ODUyNzksMTQuMzc5NzIxNiBMOC4wMjU1MjA1MiwxNC43OTM5OTY2IFoiIGlkPSJsIj48L3BhdGg+CiAgICAgICAgPC9nPgogICAgPC9nPgo8L3N2Zz4=");
                background-repeat: no-repeat;
                background-size: ${remCalc(17)};
                background-position: 0 ${remCalc(35)};
                padding-left: ${remCalc(30)};
                margin-left: ${props => props.theme.space[5]};

                /* Tablet considerations */
                ${media.md.only`
                    margin-left: 17%;
                `}

                /* Desktop considerations */
                ${media.lg.up`
                    left: calc(33% + ${remCalc(5)});
                    margin-left: ${remCalc(80)};
                `}

                /* XL Desktop considerations */
                ${media.xl.up`
                    left: calc(${remCalc(1280)} * 33 / 100 + ${remCalc(5)});
                `}
                height: ${remCalc(176)};
                ${media.sm.down`
                    height: ${remCalc(84)}`}
                line-height: ${remCalc(83)};
                width: 100%;
                font-weight: ${props => props.theme.fontWeights.semiBold};
                color: ${props => props.theme.colors.primary};
                content: "Back";
                text-align: left;
            }
        }
        input:not(:checked):focus + label, input:not(:checked):active + label {
            outline: -moz-mac-focusring solid ${remCalc(2)};
            @media (-webkit-min-device-pixel-ratio: 0) {
                outline: -webkit-focus-ring-color auto ${remCalc(5)};
            }
            &:after{
                outline: none;
                @media (-webkit-min-device-pixel-ratio: 0) {
                    outline: none;
                }
            }
        }


    }
    &.info-message {
        /* If a field has class .info-message, it generally works in conjunction with a trigger (above) that is set on another field. Because the back button on this .info-message field is an absolutely positioned :after element of the .info-message-trigger, we need adequate padding to ensure the :after tag does not cover any relevant content within the info-message: */
        /* Mobile first approach */
        overflow-y: auto;
        position: fixed;
        /* cannot control the height of the page and stop the body from scrolling => can't support showing the header in this scenario */
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: ${props => props.theme.colors.brilliant};
        z-index: 1;
        padding: ${remCalc(120)} ${remCalc(32)} ${remCalc(160)};

        /* Tablet considerations */
        ${media.md.only`
            padding: ${remCalc(180)} 17% ${remCalc(184)};
        `}

        /* Desktop considerations */
        ${media.lg.up`
            padding-left: ${remCalc(80)};
            padding-right: ${remCalc(80)};
            width: ${remCalc(500)};
            padding-top: calc(${remCalc(60)} + ${remCalc(120)});
            left: calc(33% + ${remCalc(5)});
        `}

        /* XL Desktop considerations */
        ${media.xl.up`
            left: calc(${remCalc(1280)} * 33 / 100 + ${remCalc(5)});
        `}


        > div {
            position: relative;
            ${"" /* margin-left: auto;
            margin-right: auto; */}
            max-width: ${remCalc(500)};
            img {
                width: 80%;
                height: auto;
            }
        }
    }
    &.fadein{
        -webkit-animation: fadein 2s; /* Safari, Chrome and Opera > 12.1 */
        -moz-animation: fadein 2s; /* Firefox < 16 */
        -ms-animation: fadein 2s; /* Internet Explorer */
         -o-animation: fadein 2s; /* Opera < 12.1 */
            animation: fadein 2s;
    }
    @keyframes fadein {
        from { opacity: 0; }
        to   { opacity: 1; }
    }
    /* Firefox < 16 */
    @-moz-keyframes fadein {
        from { opacity: 0; }
        to   { opacity: 1; }
    }

    /* Safari, Chrome and Opera > 12.1 */
    @-webkit-keyframes fadein {
        from { opacity: 0; }
        to   { opacity: 1; }
    }

    /* Internet Explorer */
    @-ms-keyframes fadein {
        from { opacity: 0; }
        to   { opacity: 1; }
    }

    /* Opera < 12.1 */
    @-o-keyframes fadein {
        from { opacity: 0; }
        to   { opacity: 1; }
    }

    &.poa-img-alignment{
        img {
            max-width: 100%;
        }
        ${media.md.up`
            img{
                float: left;
                max-width: ${remCalc(200)};
                margin-right: ${remCalc(24)};
            }
        `}
    }
    &.img-max-width-100{
        img {
            max-width: 100%;
        }
    }
    &.img-border-monotone{
        img {
            border: ${remCalc(1)} solid ${props =>
    props.theme.colors.monotoneMid};
        }
    }
    &.u-inline-layout{
        flex-wrap: wrap;
        .inline-group{
            order: 1;
            display: flex;
            flex-direction: column;
            align-self: center;
            flex: 0 1 25%;
            ${LabelContainer}{
                padding-bottom: 0;
            }
            ${FieldDescription}{
                margin-bottom: 0;
            }
        }
    }
    & > *:not(${LabelContainer}):not(${FieldDescription}):not(${FieldError}):not(input):not(textarea):not(select):not(.react-select-container):not(.react-aria-Select):not(.react-aria-ComboBox):not(.react-datepicker-wrapper):not(.react-slider):not(.phone-field):not(.instructions):not(.years-months-field):not(.prefix):not(.postfix):not(.radio):not(.checkbox):not(.inline-group):not(fieldset):not(.inputWrapper) {
        order: 5;
    }

    /* The field control itself is always third in the order */
    & input,
    & textarea,
    & select,
    & .react-select-container,
    & .react-aria-Select,
    & .react-aria-ComboBox,
    & .react-datepicker-wrapper,
    & .react-slider,
    & .phone-field,
    & .radio,
    & .checkbox,
    & .instructions,
    & .years-months-field,
    & .inputWrapper,
    & .prefix,
    & .postfix {
        order: 3;
    }

    & .react-aria-TextField input {
        width: 100%;
    }

    & .react-aria-TextField.readonly {
        border: 1px solid ${solasColors.textInput.outerField.stroke.color.readOnly};
        border-radius: ${solasColors.textInput.outerField.radius};

        & * {
            outline: none !important;
            cursor: not-allowed;
        }

        & *:hover {
            outline: none !important;
        }
    }

    &.double-input > *{
        order: inherit;
    }

    & .react-aria-ComboBox input {
        order: unset;
    }

    &.error {
        input,
        textarea,
        select, .react-select__control{
            border-color: ${props => props.theme.colors.alert};
            &:focus {
                border-color: ${props => props.theme.colors.alert};
            }
        }
    }

    & .prefix{
        width: 100%;
        display: flex;
        align-items: center;
        > div{
            order: 1;
            width: ${remCalc(48)};
            min-width: ${remCalc(48)};
            text-align: center;
            background: ${props => props.theme.colors.brilliant};
            border: ${remCalc(1)} solid ${props =>
    props.theme.colors.monotoneMid};
            border-right: 0;
            transition: border 500ms ease-out;
            height: ${remCalc(48)};
            line-height: ${remCalc(48)};
            -webkit-border-top-left-radius: ${remCalc(1)};
            -webkit-border-bottom-left-radius: ${remCalc(1)};
            -moz-border-radius-topleft: ${remCalc(1)};
            -moz-border-radius-bottomleft: ${remCalc(1)};
            border-top-left-radius: ${remCalc(1)};
            border-bottom-left-radius: ${remCalc(1)};
            color: ${props => props.theme.colors.monotoneDark};
            position: relative;
            box-sizing: border-box;
            &:after{
                width: ${remCalc(1)};
                height: ${remCalc(30)};
                content: '';
                background: ${props => props.theme.colors.monotoneMid};
                position: absolute;
                top: ${remCalc(8)};
                right: 0;
            }
        }
        input{
            order: 2;
            -webkit-border-top-left-radius: 0;
            -webkit-border-bottom-left-radius: 0;
            -moz-border-radius-topleft: 0;
            -moz-border-radius-bottomleft: 0;
            border-top-left-radius: 0;
            border-bottom-left-radius: 0;
            border-left: 0;
            &:focus{
                + div{
                    border-color: ${props => props.theme.colors.primary};
                }
            }
        }
    }
    &.readonly{
        .prefix{
            input{
                &:focus {
                    + div{
                        border-color: ${props =>
                            props.theme.colors.monotoneMidLight};
                    }
                }
            }
            >div{
                border-color: ${props => props.theme.colors.backgroundLight};
            }
        }
    }
    &.readonly.readonly-alt-style{
        .prefix{
            input{
                &:focus {
                    + div{
                        border-color: ${props =>
                            props.theme.colors.monotoneMidLight};
                    }
                }
            }
            >div{
                border-color: ${props => props.theme.colors.monotoneMidLight};
                background: ${props => props.theme.colors.monotoneMidLight};
            }
        }
    }
    & .postfix:not(.hide-calendar-icon){
        width: 100%;
        display: flex;
        align-items: center;

        > *:first-child{
            order: 1;
        }
        input{
            border-radius: 0;
            border-right: none;
        }
        > *:last-child{
            order: 2;
            width: ${remCalc(48)};
            min-width: ${remCalc(48)};
            height: ${remCalc(48)};
            border-width: ${remCalc(1)};
            border-style: solid;
            border-color: ${props => props.theme.colors.monotoneMid};
            border-left: 0;
            transition: border 500ms ease-out;
            -webkit-border-top-left-radius: ${remCalc(1)};
            -webkit-border-bottom-left-radius: ${remCalc(1)};
            -moz-border-radius-topleft: ${remCalc(1)};
            -moz-border-radius-bottomleft: ${remCalc(1)};
            border-top-left-radius: ${remCalc(1)};
            border-bottom-left-radius: ${remCalc(1)};
            color: ${props => props.theme.colors.monotoneDark};
            position: relative;
            display: flex;
            justify-content: center;
            align-items: center;
            box-sizing: border-box;
            &:after{
                width: ${remCalc(1)};
                height: ${remCalc(30)};
                content: '';
                background: ${props => props.theme.colors.monotoneMid};
                position: absolute;
                top: ${remCalc(8)};
                left: 0;
            }
        }
        &:focus-within, &:focus, &:active, &.active{
            > *:first-child, > *:first-child input,
            > *:last-child{
                border-color: ${props => props.theme.colors.primary};
            }

            > *:last-child{
                color: ${props => props.theme.colors.primary};
            }
        }
    }
    & .postfix.hide-calendar-icon .boi-calendar{
        display: none;
    }

    &.small .react-aria-TextField,
    &.small .react-select-container,
    &.small > .react-aria-Select,
    &.small > .react-aria-ComboBox,
    &.small > .react-aria-ComboBox input,
    &.small .prefix,
    &.small .postfix,
    &.small.medium .prefix,
    &.small.medium .postfix{
        width: 25%;
    }

    &.medium .react-aria-TextField,
    &.medium > .react-select-container,
    &.medium > .react-aria-Select,
    &.medium > .react-aria-ComboBox,
    &.medium .prefix,
    &.medium .postfix{
        width: 48%;
    }
    &.large .react-aria-TextField,
    &.large .prefix,
    &.large .postfix,
    &.large.medium .prefix,
    &.large.medium .postfix,
    &.medium .years-months-field .react-aria-TextField {
        width: 100%;
    }
    .react-datepicker-wrapper{width: 100%;}
    ${media.sm.down`
        &.small .react-aria-TextField,
        &.small .react-select-container,
        &.small .react-aria-Select,
        &.small .react-aria-ComboBox,
        &.small .prefix,
        &.small .postfix,
        &.medium .react-aria-TextField
        &.medium .react-select-container,
        &.medium .react-aria-Select,
        &.medium .react-aria-ComboBox,
        &.medium .prefix,
        &.medium .postfix,
        &.small.medium .prefix,
        &.large.medium .prefix,
        &.small.medium .postfix,
        &.large.medium .postfix,
        &.large .react-aria-TextField
        {
            width: 100%;
        }
    `}

    &.error{
        .prefix{
            >div{
                border-color: ${props => props.theme.colors.alert};
            }
            input{
                &:focus{
                    + div{
                        border-color: ${props => props.theme.colors.alert};
                    }
                }
            }
        }
        &.focus{
            .field-error{
                display: none;
            }
        }
        .postfix{
            > *:first-child, > *:first-child input,
            > *:last-child{
                border-color: ${props => props.theme.colors.alert};
            }
            > *:last-child{
                color: ${props => props.theme.colors.alert};
                border-color: ${props => props.theme.colors.alert};
            }
        }
    }
    /* Hiding field error from appearing while input is in focus state */
    & input:focus + .field-error{
        display: none;
    }
    /* Radio button layouts */
    &.align-left {
        .radio{
            > div{
                label{
                    justify-content: left;
                }
            }
        }
    }
    &.align-left-sm {
        ${media.sm.down`
            .radio{
                > div{
                    label{
                        justify-content: left;
                    }
                }
            }
        `}
    }
    &.all-options-inline {
        .radio {
            &.layout {
                &-1 {
                    > div {
                        width: calc(1 / 1 * 100% - (1 - 1 / 1) * ${remCalc(
                            10
                        )});
                    }
                }
                &-2 {
                    > div {
                        width: calc(1 / 2 * 100% - (1 - 1 / 2) * ${remCalc(
                            10
                        )});
                    }
                }
                &-3 {
                    > div {
                        width: calc(1 / 3 * 100% - (1 - 1 / 3) * ${remCalc(
                            10
                        )});
                    }
                }
                &-4 {
                    > div {
                        width: calc(1 / 4 * 100% - (1 - 1 / 4) * ${remCalc(
                            10
                        )});
                    }
                }
                ${"" /* For screenns above mobile */}
                ${media.sm.up`
                    &-5 {
                        > div {
                            width: calc(1 / 5 * 100% - (1 - 1 / 5) * ${remCalc(
                                10
                            )});
                        }
                    }
                    &-6 {
                        > div {
                            width: calc(1 / 6 * 100% - (1 - 1 / 6) * ${remCalc(
                                10
                            )});
                        }
                    }`}
            }
        }
    }
    &.all-options-inline-except-last {
            .radio {
                &.layout {
                    &-2 {
                        > div {
                            width: calc(1 / (2 - 1) * 100% - (1 - 1 / (2 - 1)) * ${remCalc(
                                10
                            )});
                        }
                    }
                    &-3 {
                        > div {
                            width: calc(1 / (3 - 1) * 100% - (1 - 1 / (3 - 1)) * ${remCalc(
                                10
                            )});
                        }
                    }
                    &-4 {
                        > div {
                            width: calc(1 / (4 - 1) * 100% - (1 - 1 / (4 - 1)) * ${remCalc(
                                10
                            )});
                        }
                    }
                    &-5 {
                        > div {
                            width: calc(1 / (5 - 1) * 100% - (1 - 1 / (5 - 1)) * ${remCalc(
                                10
                            )});
                        }
                    }
                    &-6 {
                        > div {
                            width: calc(1 / (6 - 1) * 100% - (1 - 1 / (6 - 1)) * ${remCalc(
                                10
                            )});
                        }
                    }
                    &-7{
                        > div {
                            width: calc(1 / (7 - 1) * 100% - (1 - 1 / (7 - 1)) * 10px);
                        }
                    }
                    &-8 {
                        > div {
                            width: calc(1 / (8 - 1) * 100% - (1 - 1 / (7 - 1)) * 10px);
                        }
                    }
                    &-9 {
                        > div {
                            width: calc(1 / (9 - 1) * 100% - (1 - 1 / (7 - 1)) * 10px);
                        }
                    }
                    &-2, &-3, &-4, &-5, &-6, &-7, &-8, &-9{
                        > div:last-child{
                            width: 100%;
                        }
                    }
                }
            }
        }
    &.all-options-stacked {
        .radio {
            > div {
                width: 100%;
            }
        }
    }
    &.all-options-inline.all-options-stacked-sm, &.all-options-stacked-sm {
        ${media.sm.down`
            .radio {
                > div {
                    width: 100%;
                }
            }
        `}
    }
    &.all-options-inline-md {
        .radio {
            &.layout {
                ${media.md.up`
                    &-1 {
                        > div {
                            width: calc(1 / 1 * 100% - (1 - 1 / 1) * ${remCalc(
                                10
                            )});
                        }
                    }
                    &-2 {
                        > div {
                            width: calc(1 / 2 * 100% - (1 - 1 / 2) * ${remCalc(
                                10
                            )});
                        }
                    }
                    &-3 {
                        > div {
                            width: calc(1 / 3 * 100% - (1 - 1 / 3) * ${remCalc(
                                10
                            )});
                        }
                    }
                    &-4 {
                        > div {
                            width: calc(1 / 4 * 100% - (1 - 1 / 4) * ${remCalc(
                                10
                            )});
                        }
                    }
                    &-5 {
                        > div {
                            width: calc(1 / 5 * 100% - (1 - 1 / 5) * ${remCalc(
                                10
                            )});
                        }
                    }
                    &-6 {
                        > div {
                            width: calc(1 / 6 * 100% - (1 - 1 / 6) * ${remCalc(
                                10
                            )});
                        }
                    }
                    &-7 {
                        > div {
                            width: calc(1 / 7 * 100% - (1 - 1 / 7) * 10px);
                        }
                    }
                    &-8 {
                        > div {
                            width: calc(1 / 8 * 100% - (1 - 1 / 8) * 10px);
                        }
                    }
                    &-9 {
                        > div {
                            width: calc(1 / 9 * 100% - (1 - 1 / 9) * 10px);
                        }
                    }
                `}
            }
        }
    }
    &.three-by-three{
        .radio {
            &.layout {
                &-1, &-2, &-3, &-4, &-5, &-6, &-7, &-8, &-9, &-10, &-11, &-12 {
                    > div {
                        width: calc(1 / 3 * 100% - (1 - 1 / 3) * ${remCalc(
                            10
                        )});
                    }
                }
            }
            &:after{
                content: " ";
                width: calc(1 / 3 * 100% - (1 - 1 / 3) * ${remCalc(10)});
            }
        }
    }
    &.radios-as-primary-secondary{
        .radio{
            > div{
                .field-label{
                    background: none;
                    border-color: ${props => props.theme.colors.primary};
                    color: ${props => props.theme.colors.primary};
                    font-weight: ${props => props.theme.fontWeights.semiBold};
                }
                &:first-child{
                    .field-label{
                        background: ${props => props.theme.colors.primary};
                        border-color: ${props => props.theme.colors.primary};
                        color: ${props => props.theme.colors.brilliant};
                    }
                }
            }
        }
    }
    &.u-radio-traditional{
        .radio{
            > div{
                .field-label {
                    padding: 0;
                    border: 0;
                    padding-left: ${remCalc(32)};
                    margin-bottom: ${remCalc(14)};
                    display: flex;
                    justify-content: start;
                    text-align: left;
                    color: ${props => props.theme.colors.monotoneDark};
                    cursor: pointer;
                    background: transparent;
                    position: relative;
                    strong {
                        font-weight: ${props =>
                            props.theme.fontWeights.semiBold};
                    }
                    &:before {
                        content: " ";
                        position: absolute;
                        box-sizing: border-box;
                        margin: 0;
                        left: 0;
                        top: ${remCalc(2)};
                        width: ${remCalc(18)};
                        height: ${remCalc(18)};
                        border: ${remCalc(2)} solid
                            ${props => props.theme.colors.primary};
                        border-radius: 100%;
                        background: ${props => props.theme.colors.brilliant};
                    }
                    &:after {
                        content: " ";
                        position: absolute;
                        left: ${remCalc(4)};
                        top: ${remCalc(6)};
                        width: ${remCalc(10)};
                        height: ${remCalc(10)};
                        background: ${props => props.theme.colors.primary};
                        border-radius: 100%;
                        -webkit-transition: all 0.15s ease;
                        transition: all 0.15s ease;
                        opacity: 0;
                        -webkit-transform: scale(0);
                        transform: scale(0);
                    }
                }
                input:focus + label,
                input:focus ~ label {
                    outline: none;
                    box-shadow: none;
                    @media (-webkit-min-device-pixel-ratio: 0) {
                        outline: none;
                        box-shadow: none;
                    }
                    &:before {
                        ${"" /* outline: none; */}
                        box-shadow: 0 0 ${remCalc(2)} ${remCalc(2)} ${props =>
    props.theme.colors.primary};
                        transition: box-shadow 150ms ease-in;
                        @media (-webkit-min-device-pixel-ratio: 0) {
                            ${"" /* outline: none; */}
                            box-shadow: 0 0 ${remCalc(2)} ${remCalc(
    2
)} ${props => props.theme.colors.primary};
                        }
                    }
                }
                input:checked + label,
                input:checked ~ label {
                    &:after {
                        opacity: 1;
                        -webkit-transform: scale(1);
                        transform: scale(1);
                    }
                }
            }
            &.layout {
                &-1, &-2, &-3, &-4, &-5, &-6, &-7, &-8, &-9, &-10, &-11, &-12 {
                    > div {
                        width: 100%;
                    }
                }
            }
        }
    }

    /* Application stages within HTML blocks */
    &.application-stage {
        ol {
            color: ${props => props.theme.colors.monotoneMidDark};
            font-size: ${remCalc(16)};
            li {
                padding: ${remCalc(10)} 0;
            }
            &.tick-circle-full {
                list-style: none;
                padding-left: 0;
                li {
                    background-image: url("data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyB3aWR0aD0iMjVweCIgaGVpZ2h0PSIyNXB4IiB2aWV3Qm94PSIwIDAgMjUgMjUiIHZlcnNpb249IjEuMSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB4bWxuczp4bGluaz0iaHR0cDovL3d3dy53My5vcmcvMTk5OS94bGluayI+CiAgICA8IS0tIEdlbmVyYXRvcjogU2tldGNoIDUzLjEgKDcyNjMxKSAtIGh0dHBzOi8vc2tldGNoYXBwLmNvbSAtLT4KICAgIDx0aXRsZT5saXN0LXRpY2stY2lyY2xlLWZpbGw8L3RpdGxlPgogICAgPGRlc2M+Q3JlYXRlZCB3aXRoIFNrZXRjaC48L2Rlc2M+CiAgICA8ZyBpZD0ibGlzdC10aWNrLWNpcmNsZS1maWxsIiBzdHJva2U9Im5vbmUiIHN0cm9rZS13aWR0aD0iMSIgZmlsbD0ibm9uZSIgZmlsbC1ydWxlPSJldmVub2RkIj4KICAgICAgICA8cGF0aCBkPSJNMjQuMjQ5OTk5NCwxMi4yNTAwMzI4IEMyNC4yNDk5OTk0LDE4Ljg3NzQ1ODQgMTguODc3NDI1MSwyNC4yNTAwMzI4IDEyLjI0OTk5OTQsMjQuMjUwMDMyOCBDNS42MjI1NzM3NSwyNC4yNTAwMzI4IDAuMjQ5OTk5NDA0LDE4Ljg3NzQ1ODQgMC4yNDk5OTk0MDQsMTIuMjUwMDMyOCBDMC4yNDk5OTk0MDQsNS42MjI2MDcxMyA1LjYyMjU3Mzc1LDAuMjUwMDMyNzgzIDEyLjI0OTk5OTQsMC4yNTAwMzI3ODMgQzE4Ljg3NzQyNTEsMC4yNTAwMzI3ODMgMjQuMjQ5OTk5NCw1LjYyMjYwNzEzIDI0LjI0OTk5OTQsMTIuMjUwMDMyOCBaIE0xMC44NjE5ODkxLDE4LjYwMzk1NzkgTDE5Ljc2NTIxNDksOS43MDA3MzIxMyBDMjAuMDY3NDkyNSw5LjM5ODQwNzI4IDIwLjA2NzQ5MjUsOC45MDgxNTcxNSAxOS43NjUyMTQ5LDguNjA1ODMyMyBMMTguNjcwMzE1MSw3LjUxMDk3OTczIEMxOC4zNjgwMzc1LDcuMjA4NjA3NjMgMTcuODc3Nzg3Myw3LjIwODYwNzYzIDE3LjU3NTQxNTIsNy41MTA5Nzk3MyBMMTAuMzE0NTE1NSwxNC43NzE4Nzk0IEw2LjkyNDU4MzU4LDExLjM4MTkwMDIgQzYuNjIyMjU4NzMsMTEuMDc5NTc1NCA2LjEzMjAwODYsMTEuMDc5NTc1NCA1LjgyOTY4Mzc1LDExLjM4MTkwMDIgTDQuNzM0NzgzOTMsMTIuNDc2NzUyOCBDNC40MzI1MDYzMywxMi43NzkwNzc2IDQuNDMyNTA2MzMsMTMuMjY5MzI3OCA0LjczNDc4MzkzLDEzLjU3MTY1MjYgTDkuNzY3MDQxOTksMTguNjAzOTEwNyBDMTAuMDY5NDE0MSwxOC45MDYyMzU1IDEwLjU1OTYxNywxOC45MDYyMzU1IDEwLjg2MTk4OTEsMTguNjAzOTU3OSBaIiBpZD0i74GYIiBmaWxsPSIjMTA2OTg4IiBmaWxsLXJ1bGU9Im5vbnplcm8iPjwvcGF0aD4KICAgIDwvZz4KPC9zdmc+");
                    background-repeat: no-repeat;
                    background-size: ${remCalc(20)} ${remCalc(20)};
                    background-position: 0 ${remCalc(11)};
                    padding-left: ${remCalc(30)};
                    margin-left: 0;
                }
            }

            counter-reset: list-counter;
            list-style: none;
            padding-left: 0;
            border-left: 0 solid
                ${props => props.theme.colors.monotoneMidLight};
            li {
                padding: 0;
                margin-left: 0;
                position: relative;
                padding-top: ${props => props.theme.space[7]};
                font-weight: ${props => props.theme.fontWeights.regular};
                color: ${solasColors.themeColor.base500};
                transition: all 500ms ease-in;
                display: flex;
                align-items: center;
                &:before {
                    counter-increment: list-counter;
                    content: counter(list-counter);
                    width: ${remCalc(40)};
                    min-width: ${remCalc(40)};
                    height: ${remCalc(40)};
                    line-height: ${remCalc(40)};
                    font-size: ${remCalc(16)};
                    font-weight: ${props => props.theme.fontWeights.regular};
                    border-radius: 100%;
                    background-color: ${props => props.theme.colors.brilliant};
                    text-align: center;
                    border: ${remCalc(2)} solid
                        ${props => props.theme.colors.monotoneMidDark};
                    color: ${props => props.theme.colors.monotoneMidDark};
                    transition: background-color 500ms, border-color 500ms, color 500ms;
                    margin-right: ${remCalc(16)};
                    z-index: 1;
                }
                &:after {
                    position: absolute;
                    content: " ";
                    width: ${remCalc(2)};
                    height: 100%;
                    background: ${props => props.theme.colors.monotoneMid};
                    left: ${remCalc(21)};
                    top: ${remCalc(-5)};
                    transition: height 500ms ease-in;
                    margin-top: -${remCalc(1)};
                }
                &:first-child {
                    &:after {
                        height: 0;
                    }
                }
                &.active {
                    color: ${props => props.theme.colors.primary};
                    &:before {
                        border-color: ${props => props.theme.colors.primary};
                        color: ${props => props.theme.colors.primary};
                    }
                }
            }
        }

        &.stage-one li:nth-child(1),
        &.stage-two li:nth-child(2),
        &.stage-three li:nth-child(3),
        &.stage-four li:nth-child(4),
        &.stage-five li:nth-child(5),
        &.stage-six li:nth-child(6) {
            color: ${props => props.theme.colors.monotoneDark};
            font-weight: ${props => props.theme.fontWeights.semiBold};
            &:before {
                border-color: ${props => props.theme.colors.primary};
                color: ${props => props.theme.colors.primary};
                font-weight: ${props => props.theme.fontWeights.semiBold};
            }
            &:after {
                background: ${props => props.theme.colors.primary};
            }
        }

        &.stage-two li:nth-child(1),
        &.stage-three li:nth-child(1),
        &.stage-three li:nth-child(2),
        &.stage-four li:nth-child(1),
        &.stage-four li:nth-child(2),
        &.stage-four li:nth-child(3),
        &.stage-five li:nth-child(1),
        &.stage-five li:nth-child(2),
        &.stage-five li:nth-child(3),
        &.stage-five li:nth-child(4),
        &.stage-six li:nth-child(1),
        &.stage-six li:nth-child(2),
        &.stage-six li:nth-child(3),
        &.stage-six li:nth-child(4),
        &.stage-six li:nth-child(5)
        {
            color: ${props => props.theme.colors.monotoneDark};
            font-weight: ${props => props.theme.fontWeights.regular};
            &:before {
                content: " ";
                border-color: ${props => props.theme.colors.primary};
                background-color: ${props => props.theme.colors.primary};
                background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='16' xmlns:v='https://vecta.io/nano'%3E%3Cpath d='M7.06 16L.304 9.503c-.397-.386-.406-1.022-.02-1.419s1.022-.406 1.419-.02l5.237 5.098L18.234.346c.364-.42 1-.464 1.419-.1s.464 1 .1 1.419L7.06 16z' fill='%23fff'/%3E%3C/svg%3E");
                background-repeat: no-repeat;
                background-size: ${remCalc(20)};
                background-position: center center;
                color: ${props => props.theme.colors.brilliant};
            }
            &:after {
                background: ${props => props.theme.colors.primary};
            }
        }
    }
    /* Alert styling for HTML blocks */
    &.alert{
        padding: ${remCalc(12)} ${remCalc(16)};
        color: ${props => props.theme.colors.monotoneDark};
        min-height: ${remCalc(21)};
        border-radius: ${props => props.theme.radii[2]};
        display: flex;
        margin-bottom: 0;
        margin-bottom: ${remCalc(32)};
        justify-content: start;
        flex-direction: row;
        text-align: center;
        > * {
            flex: 1;
        }
        > div{
            color: ${props => props.theme.colors.monotoneDark};
            p, ul, li{
                font-size: ${props => props.theme.fontSizes[1]};
            }
            p{
                color: ${props => props.theme.colors.monotoneDark};
                margin-bottom: ${remCalc(12)};
                &:last-child{
                    margin-bottom: 0;
                }
            }
        }
        &.info{
            background: ${props => props.theme.colors.brilliant};
            border: ${remCalc(1)} solid ${props => props.theme.colors.primary};
        }
        &.success {
            background: ${props => props.theme.colors.secondary};
            border: ${remCalc(1)} solid ${props =>
    props.theme.colors.secondary};
        }
        &.error {
            background: ${props => props.theme.colors.brilliant};
            border: ${remCalc(1)} solid ${props => props.theme.colors.alert};
        }
        &.alert-alternative{
            text-align: left;
            position: relative;
            padding-left: ${remCalc(52)};
            > div{
                p, ul, ol, li{
                    font-size: font-size: ${props => props.theme.fontSizes[1]};
                    &:last-child{
                        margin-bottom: 0;
                        padding-bottom: 0;
                    }
                }
                ul, ol{
                    margin-top: 0;
                    li{
                        padding-top: 0;
                        padding-bottom: ${remCalc(14)};
                    }
                }
                p{
                    margin-bottom: ${remCalc(14)};
                    &:last-child{
                        margin-bottom: 0;
                    }
                }
            }
            &:before {
                margin-top: ${remCalc(-4)};
                content: ' ';
                height: ${remCalc(32)};
                width: ${remCalc(32)};
                left: ${remCalc(8)};
                position: absolute;
            }
            &.info:before{
                background: url("data:image/svg+xml;base64,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") no-repeat left center;
            }
            &.success:before{
                background: url("data:image/svg+xml;base64,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") no-repeat left center;
            }
            &.error:before{
                 background: url("data:image/svg+xml;base64,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") no-repeat left center;
            }
        }
    }
    /* Lists for HTML blocks */
    div > ul > li, div > ol > li{
        padding: ${remCalc(10)} 0 ${remCalc(10)} ${remCalc(8)};
        > ul > li {
            padding: ${remCalc(10)} 0 ${remCalc(10)} ${remCalc(8)};
        }
        ul > li:last-child {
            padding-bottom: 0;
        }
    }
    &.unordered-list, &.ordered-list, &.check-list, &.negative-list{
        > div {
            font-size: ${props => props.theme.fontSizes[2]};
            font-weight: ${props => props.theme.fontWeights.regular};
            > ul, > ol{
                list-style: none;
                padding-left: 0;
                color: ${props => props.theme.colors.monotoneDark};
                > li{
                    position: relative;
                    margin-left: 0;
                    padding: ${remCalc(10)} 0;
                    padding-left: ${remCalc(32)};
                }
            }
        }
    }
    &.check-list-simple, &.check-list, &.negative-list-simple, &.negative-list{
        > div > ul{
            list-style: none;
            padding-left: 0;
            > li {
                position: relative;
                margin-left: 0;
                padding: ${remCalc(10)} 0 ${remCalc(10)} ${remCalc(32)};
                &:before{
                    height: ${remCalc(16)};
                    position: absolute;
                    top: ${remCalc(20)};
                    left: ${remCalc(0)};
                    transform: translateY(-50%);
                }
                ul > li:last-child {
                    padding-bottom: 0;
                }
            }
        }
    }
    &.check-list-simple > div > ul > li:before,
    &.check-list > div > ul > li:before{
        content: url("data:image/svg+xml,%3Csvg width='16px' height='14px' viewBox='0 0 16 14' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Ctitle%3Echeck-list%3C/title%3E%3Cg id='check-list' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cpath d='M14.100372,0.89044387 C14.4683813,0.44182986 15.1396248,0.36909516 15.5996364,0.72798636 C16.0085356,1.04700077 16.1128983,1.59967135 15.8725772,2.03362454 L15.7662214,2.19010388 L6.48843089,13.5 L0.312418666,7.47700935 C-0.104139555,7.07077209 -0.104139555,6.41213136 0.312418666,6.0058941 C0.682692642,5.64479431 1.25746329,5.60467211 1.67318097,5.8855275 L1.82090943,6.0058941 L6.31038451,10.3843567 L14.100372,0.89044387 Z' id='Combined-Shape' fill='%230000FF' fill-rule='nonzero'%3E%3C/path%3E%3C/g%3E%3C/svg%3E");
    }
    &.negative-list-simple > div > ul > li:before,
    &.negative-list > div > ul > li:before{
        content: url("data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyB3aWR0aD0iMTZweCIgaGVpZ2h0PSIxNnB4IiB2aWV3Qm94PSIwIDAgMTYgMTYiIHZlcnNpb249IjEuMSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB4bWxuczp4bGluaz0iaHR0cDovL3d3dy53My5vcmcvMTk5OS94bGluayI+CiAgICA8dGl0bGU+Q29tYmluZWQgU2hhcGU8L3RpdGxlPgogICAgPGcgaWQ9IlN5bWJvbHMiIHN0cm9rZT0ibm9uZSIgc3Ryb2tlLXdpZHRoPSIxIiBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPgogICAgICAgIDxnIGlkPSJAYS9saXN0LWl0ZW0vUHJpbWFyeS1yZWd1bGFyLW5lZ2F0aXZlLTIiIHRyYW5zZm9ybT0idHJhbnNsYXRlKDAuMDAwMDAwLCAtNC4wMDAwMDApIiBmaWxsPSIjQjIwMDAwIiBmaWxsLXJ1bGU9Im5vbnplcm8iPgogICAgICAgICAgICA8cGF0aCBkPSJNMC4yOTI4OTMyMTksNC4yOTI4OTMyMiBDMC42ODM0MTc1MTEsMy45MDIzNjg5MyAxLjMxNjU4MjQ5LDMuOTAyMzY4OTMgMS43MDcxMDY3OCw0LjI5Mjg5MzIyIEw4LDEwLjU4NSBMMTQuMjkyODkzMiw0LjI5Mjg5MzIyIEMxNC42NTMzNzcyLDMuOTMyNDA5MjYgMTUuMjIwNjA4MiwzLjkwNDY3OTcyIDE1LjYxMjg5OTQsNC4yMDk3MDQ2MSBMMTUuNzA3MTA2OCw0LjI5Mjg5MzIyIEMxNi4wOTc2MzExLDQuNjgzNDE3NTEgMTYuMDk3NjMxMSw1LjMxNjU4MjQ5IDE1LjcwNzEwNjgsNS43MDcxMDY3OCBMOS40MTQsMTIgTDE1LjcwNzEwNjgsMTguMjkyODkzMiBDMTYuMDY3NTkwNywxOC42NTMzNzcyIDE2LjA5NTMyMDMsMTkuMjIwNjA4MiAxNS43OTAyOTU0LDE5LjYxMjg5OTQgTDE1LjcwNzEwNjgsMTkuNzA3MTA2OCBDMTUuMzE2NTgyNSwyMC4wOTc2MzExIDE0LjY4MzQxNzUsMjAuMDk3NjMxMSAxNC4yOTI4OTMyLDE5LjcwNzEwNjggTDgsMTMuNDE0IEwxLjcwNzEwNjc4LDE5LjcwNzEwNjggQzEuMzQ2NjIyODIsMjAuMDY3NTkwNyAwLjc3OTM5MTc2NCwyMC4wOTUzMjAzIDAuMzg3MTAwNTU3LDE5Ljc5MDI5NTQgTDAuMjkyODkzMjE5LDE5LjcwNzEwNjggQy0wLjA5NzYzMTA3MjksMTkuMzE2NTgyNSAtMC4wOTc2MzEwNzI5LDE4LjY4MzQxNzUgMC4yOTI4OTMyMTksMTguMjkyODkzMiBMNi41ODUsMTIgTDAuMjkyODkzMjE5LDUuNzA3MTA2NzggQy0wLjA2NzU5MDc0MjgsNS4zNDY2MjI4MiAtMC4wOTUzMjAyNzgzLDQuNzc5MzkxNzYgMC4yMDk3MDQ2MTIsNC4zODcxMDA1NiBMMC4yOTI4OTMyMTksNC4yOTI4OTMyMiBaIiBpZD0iQ29tYmluZWQtU2hhcGUiPjwvcGF0aD4KICAgICAgICA8L2c+CiAgICA8L2c+Cjwvc3ZnPg==");
    }
    &.u-list-alpha-upper{
        > div > ol{ list-style-type: upper-alpha; }
    }
    &.u-list-alpha-lower{
        > div > ol{ list-style-type: lower-alpha; }
    }
    &.u-list-roman-upper{
        > div > ol{ list-style-type: upper-roman; }
    }
    &.u-list-roman-lower{
        > div > ol{ list-style-type: lower-roman; }
    }
    &.check-list-simple-sub-list, &.check-list-sub-list,
    &.negative-list-simple-sub-list, &.negative-list-sub-list{
        > div > ul > li > ul, > div > ol > li > ul{
            list-style: none;
            padding-left: 0;
            > li {
                position: relative;
                margin-left: 0;
                padding: ${remCalc(10)} 0 ${remCalc(10)} ${remCalc(32)};
                &:before{
                    height: ${remCalc(16)};
                    position: absolute;
                    top: ${remCalc(20)};
                    left: ${remCalc(0)};
                    transform: translateY(-50%);
                }
            }
        }
    }
    &.check-list-simple-sub-list, &.check-list-sub-list{
        > div > ul > li > ul > li:before, > div > ol > li > ul > li:before{
            content: url("data:image/svg+xml,%3Csvg width='16px' height='14px' viewBox='0 0 16 14' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Ctitle%3Echeck-list%3C/title%3E%3Cg id='check-list' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cpath d='M14.100372,0.89044387 C14.4683813,0.44182986 15.1396248,0.36909516 15.5996364,0.72798636 C16.0085356,1.04700077 16.1128983,1.59967135 15.8725772,2.03362454 L15.7662214,2.19010388 L6.48843089,13.5 L0.312418666,7.47700935 C-0.104139555,7.07077209 -0.104139555,6.41213136 0.312418666,6.0058941 C0.682692642,5.64479431 1.25746329,5.60467211 1.67318097,5.8855275 L1.82090943,6.0058941 L6.31038451,10.3843567 L14.100372,0.89044387 Z' id='Combined-Shape' fill='%230000FF' fill-rule='nonzero'%3E%3C/path%3E%3C/g%3E%3C/svg%3E");
        }
    }
    &.negative-list-simple-sub-list, &.negative-list-sub-list{
        > div > ul > li > ul > li:before, > div > ol > li > ul > li:before{
            content: url("data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyB3aWR0aD0iMTZweCIgaGVpZ2h0PSIxNnB4IiB2aWV3Qm94PSIwIDAgMTYgMTYiIHZlcnNpb249IjEuMSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB4bWxuczp4bGluaz0iaHR0cDovL3d3dy53My5vcmcvMTk5OS94bGluayI+CiAgICA8dGl0bGU+Q29tYmluZWQgU2hhcGU8L3RpdGxlPgogICAgPGcgaWQ9IlN5bWJvbHMiIHN0cm9rZT0ibm9uZSIgc3Ryb2tlLXdpZHRoPSIxIiBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPgogICAgICAgIDxnIGlkPSJAYS9saXN0LWl0ZW0vUHJpbWFyeS1yZWd1bGFyLW5lZ2F0aXZlLTIiIHRyYW5zZm9ybT0idHJhbnNsYXRlKDAuMDAwMDAwLCAtNC4wMDAwMDApIiBmaWxsPSIjQjIwMDAwIiBmaWxsLXJ1bGU9Im5vbnplcm8iPgogICAgICAgICAgICA8cGF0aCBkPSJNMC4yOTI4OTMyMTksNC4yOTI4OTMyMiBDMC42ODM0MTc1MTEsMy45MDIzNjg5MyAxLjMxNjU4MjQ5LDMuOTAyMzY4OTMgMS43MDcxMDY3OCw0LjI5Mjg5MzIyIEw4LDEwLjU4NSBMMTQuMjkyODkzMiw0LjI5Mjg5MzIyIEMxNC42NTMzNzcyLDMuOTMyNDA5MjYgMTUuMjIwNjA4MiwzLjkwNDY3OTcyIDE1LjYxMjg5OTQsNC4yMDk3MDQ2MSBMMTUuNzA3MTA2OCw0LjI5Mjg5MzIyIEMxNi4wOTc2MzExLDQuNjgzNDE3NTEgMTYuMDk3NjMxMSw1LjMxNjU4MjQ5IDE1LjcwNzEwNjgsNS43MDcxMDY3OCBMOS40MTQsMTIgTDE1LjcwNzEwNjgsMTguMjkyODkzMiBDMTYuMDY3NTkwNywxOC42NTMzNzcyIDE2LjA5NTMyMDMsMTkuMjIwNjA4MiAxNS43OTAyOTU0LDE5LjYxMjg5OTQgTDE1LjcwNzEwNjgsMTkuNzA3MTA2OCBDMTUuMzE2NTgyNSwyMC4wOTc2MzExIDE0LjY4MzQxNzUsMjAuMDk3NjMxMSAxNC4yOTI4OTMyLDE5LjcwNzEwNjggTDgsMTMuNDE0IEwxLjcwNzEwNjc4LDE5LjcwNzEwNjggQzEuMzQ2NjIyODIsMjAuMDY3NTkwNyAwLjc3OTM5MTc2NCwyMC4wOTUzMjAzIDAuMzg3MTAwNTU3LDE5Ljc5MDI5NTQgTDAuMjkyODkzMjE5LDE5LjcwNzEwNjggQy0wLjA5NzYzMTA3MjksMTkuMzE2NTgyNSAtMC4wOTc2MzEwNzI5LDE4LjY4MzQxNzUgMC4yOTI4OTMyMTksMTguMjkyODkzMiBMNi41ODUsMTIgTDAuMjkyODkzMjE5LDUuNzA3MTA2NzggQy0wLjA2NzU5MDc0MjgsNS4zNDY2MjI4MiAtMC4wOTUzMjAyNzgzLDQuNzc5MzkxNzYgMC4yMDk3MDQ2MTIsNC4zODcxMDA1NiBMMC4yOTI4OTMyMTksNC4yOTI4OTMyMiBaIiBpZD0iQ29tYmluZWQtU2hhcGUiPjwvcGF0aD4KICAgICAgICA8L2c+CiAgICA8L2c+Cjwvc3ZnPg==");
        }
    }
    &.u-list-alpha-upper-sub-list{
        > div > ol, > div > ul{
            > li > ol{
                list-style-type: upper-alpha;
            }
        }
    }
    &.u-list-alpha-lower-sub-list{
        > div > ol, > div > ul{
            > li > ol{
                list-style-type: lower-alpha;
            }
        }
    }

    &.u-list-roman-upper-sub-list{
        > div > ol, > div > ul{
            > li > ol{
                list-style-type: upper-roman;
            }
        }
    }

    &.u-list-roman-lower-sub-list{
        > div > ol, > div > ul{
            > li > ol{
                list-style-type: lower-roman;
            }
        }
    }

    &.u-h1-reduced-padding{
        h1{ margin-bottom: ${remCalc(10)}; }
    }
    &.u-h2-reduced-padding {
        h2{ margin-bottom: ${remCalc(10)}; }
    }
    &.u-p-reduced-padding {
        p{ margin-bottom: ${remCalc(10)}; }
        ${media.xs.down`
            p{
                margin-bottom: ${remCalc(4)};
            }
        `}
    }
    &.u-h1-no-padding{
        h1{
            margin-bottom: 0;
        }
    }
    &.u-h2-no-padding{
        h2{
            margin-bottom: 0;
        }
    }
    &.u-p-no-padding{
        p{
            margin-bottom: 0;
        }
    }
    &.result-display{
        h1, h2{
            font-size: ${props => props.theme.fontSizes[7]};
            line-height: ${props => props.theme.lineHeights[7]};
            margin-bottom: ${remCalc(20)};
        }
        p{
            margin-bottom: 0;
        }
        padding: ${remCalc(32)};
        border: ${remCalc(1)} solid ${props => props.theme.colors.primary};
        border-radius: ${props => props.theme.radius};
        text-align: center;
    }
    &.u-or-separator {
        > div{
            display: flex;
            align-items: center;
            text-align: center;
            p{
                margin-bottom: 0;
            }
            &:before, &:after{
                content: '';
                flex: 1;
                border-bottom: ${remCalc(1)} solid ${props =>
    props.theme.colors.monotoneMidLight};
            }
            &:before{
                margin-right: ${remCalc(32)};
            }
            &:after{
                margin-left: ${remCalc(32)};
            }
        }
    }
    &.u-link-as-primary, &.u-link-as-secondary{
        > div p{
            margin-bottom: 0;
        }
        > div a{
            border-radius: ${props => props.theme.radius};
            font-size: ${remCalc(16)};
            font-weight: ${props => props.theme.fontWeights.semiBold};
            border: ${remCalc(1)} solid;
            text-decoration: none;
            height: ${remCalc(60)};
            min-width: ${remCalc(200)};
            &[target="_blank"] {
                background-image: none;
            }
            display: flex;
            justify-content: center;
            align-items: center;
            padding-right: 0;
            margin-left: auto;
            margin-right: auto;
        }
        &.block{
            > div a{
                width:100%;
                ${media.sm.up`
                    width: 70%;`}
                }
        }
    }
    &.u-link-as-primary{
        > div a{
            background: ${props => props.theme.colors.primary};
            border-color: ${props => props.theme.colors.primary};
            color: ${props => props.theme.colors.brilliant};
            &[target="_blank"]{
                &:after{
                    background-image: url("data:image/svg+xml,%3C?xml version='1.0' encoding='UTF-8'?%3E %3Csvg  viewBox='0 0 16 16' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E %3Ctitle%3Ee%3C/title%3E %3Cg id='Templates' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E %3Cpath d='M5.0824769,12.0015612 C5.16844471,12.0015612 5.28743246,11.9521533 5.34810126,11.8912404 L13.8543371,3.38469938 L15.3608948,4.89095189 C15.421411,4.95097983 15.5397274,4.99968577 15.6249627,4.99968577 C15.8317799,4.99968577 15.9997787,4.83183949 15.9999619,4.62502235 L15.9999619,0.375032485 C15.9999619,0.168032246 15.831963,3.33786011e-05 15.6249627,3.33786011e-05 L11.3749729,3.33786011e-05 C11.1681557,0.000216483633 11.0003095,0.168215351 11.0003095,0.375032485 C11.0003095,0.460695122 11.0494427,0.579408218 11.1099589,0.640046501 L12.6159062,2.14660419 L4.10967038,10.6528095 C4.04878795,10.7134783 3.99938011,10.8324966 3.99938011,10.9184339 C3.99938011,11.0044017 4.04878795,11.1233894 4.10967038,11.1840582 L4.81685253,11.8912404 C4.87752133,11.9521533 4.9965396,12.0015612 5.0824769,12.0015612 Z M12.4999702,15.9999952 C13.3279712,15.9999952 13.9999666,15.3279998 13.9999666,14.4999988 L13.9999666,9.50001073 C13.9999666,9.22401041 13.7759681,9.00001192 13.4999678,9.00001192 L12.999969,9.00001192 C12.7239687,9.00001192 12.4999702,9.22401041 12.4999702,9.50001073 L12.4999702,14.3124993 C12.4999702,14.4159841 12.4159555,14.4999988 12.3124706,14.4999988 L1.68749598,14.4999988 C1.58401112,14.4999988 1.5,14.4159841 1.5,14.3124993 L1.5,3.68752459 C1.5,3.58403973 1.58401112,3.50002861 1.68749598,3.50002861 L6.4999845,3.50002861 C6.77598482,3.50002861 6.99998331,3.27602654 6.99998331,3.00002861 L6.99998331,2.50002861 C6.99998331,2.2240271 6.77598482,2.00002861 6.4999845,2.00002861 L1.5,2.00002861 C0.671995468,2.00002861 0,2.67202408 0,3.50002861 L0,14.4999988 C0,15.3279998 0.671995468,15.9999952 1.5,15.9999952 L12.4999702,15.9999952 Z' id='e' fill='%23FFFFFF' fill-rule='nonzero'%3E%3C/path%3E %3C/g%3E %3C/svg%3E");
                }
            }
            &:hover{
                background: ${props => props.theme.colors.primaryTone};
                border-color: ${props => props.theme.colors.primaryTone};
                &[target="_blank"]{
                    &:after{
                        background-image: url("data:image/svg+xml,%3C?xml version='1.0' encoding='UTF-8'?%3E %3Csvg  viewBox='0 0 16 16' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E %3Ctitle%3Ee%3C/title%3E %3Cg id='Templates' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E %3Cpath d='M5.0824769,12.0015612 C5.16844471,12.0015612 5.28743246,11.9521533 5.34810126,11.8912404 L13.8543371,3.38469938 L15.3608948,4.89095189 C15.421411,4.95097983 15.5397274,4.99968577 15.6249627,4.99968577 C15.8317799,4.99968577 15.9997787,4.83183949 15.9999619,4.62502235 L15.9999619,0.375032485 C15.9999619,0.168032246 15.831963,3.33786011e-05 15.6249627,3.33786011e-05 L11.3749729,3.33786011e-05 C11.1681557,0.000216483633 11.0003095,0.168215351 11.0003095,0.375032485 C11.0003095,0.460695122 11.0494427,0.579408218 11.1099589,0.640046501 L12.6159062,2.14660419 L4.10967038,10.6528095 C4.04878795,10.7134783 3.99938011,10.8324966 3.99938011,10.9184339 C3.99938011,11.0044017 4.04878795,11.1233894 4.10967038,11.1840582 L4.81685253,11.8912404 C4.87752133,11.9521533 4.9965396,12.0015612 5.0824769,12.0015612 Z M12.4999702,15.9999952 C13.3279712,15.9999952 13.9999666,15.3279998 13.9999666,14.4999988 L13.9999666,9.50001073 C13.9999666,9.22401041 13.7759681,9.00001192 13.4999678,9.00001192 L12.999969,9.00001192 C12.7239687,9.00001192 12.4999702,9.22401041 12.4999702,9.50001073 L12.4999702,14.3124993 C12.4999702,14.4159841 12.4159555,14.4999988 12.3124706,14.4999988 L1.68749598,14.4999988 C1.58401112,14.4999988 1.5,14.4159841 1.5,14.3124993 L1.5,3.68752459 C1.5,3.58403973 1.58401112,3.50002861 1.68749598,3.50002861 L6.4999845,3.50002861 C6.77598482,3.50002861 6.99998331,3.27602654 6.99998331,3.00002861 L6.99998331,2.50002861 C6.99998331,2.2240271 6.77598482,2.00002861 6.4999845,2.00002861 L1.5,2.00002861 C0.671995468,2.00002861 0,2.67202408 0,3.50002861 L0,14.4999988 C0,15.3279998 0.671995468,15.9999952 1.5,15.9999952 L12.4999702,15.9999952 Z' id='e' fill='%23FFFFFF' fill-rule='nonzero'%3E%3C/path%3E %3C/g%3E %3C/svg%3E");
                    }
                }
            }
        }
    }
    &.u-link-as-secondary{
        > div a{
            background: none;
            border-color: ${props => props.theme.colors.primary};
            color: ${props => props.theme.colors.primary};
            &:hover{
                border-color: ${props => props.theme.colors.primaryTone};
                color: ${props => props.theme.colors.primaryTone};
            }
        }
    }
    /* Grid layouts HTML blocks */
    &.grid-first-p-left-aligned-fifth, &.grid-first-p-left-aligned-quarter, &.grid-first-p-left-aligned-third, &.grid-first-p-left-aligned-half {
        > div{
            display: grid;
            column-gap: ${remCalc(16)};
            p:first-child{
                grid-column: 1 / 1;
                img{
                    max-width: 100%;
                }
            }
        }
    }
    &.grid-first-p-left-aligned-fifth{
        > div{
            grid-template-columns: repeat(5, minmax(0, 1fr));
            p:first-child{
                grid-row: 1 / 6;
            }
            p:not(:first-child){
                grid-column: 2 / 6;
            }
        }
    }
    &.grid-first-p-left-aligned-quarter{
        > div{
            grid-template-columns: repeat(4, minmax(0, 1fr));
            p:first-child{
                grid-row: 1 / 5;
            }
            p:not(:first-child){
                grid-column: 2 / 5;
            }
        }
    }
    &.grid-first-p-left-aligned-third{
        > div{
            grid-template-columns: repeat(3, minmax(0, 1fr));
            p:first-child{
                grid-row: 1 / 4;
            }
            p:not(:first-child){
                grid-column: 2 / 4;
            }
        }
    }
    &.grid-first-p-left-aligned-half{
        > div{
            grid-template-columns: repeat(2, minmax(0, 1fr));
            p:first-child{
                grid-row: 1 / 3;
            }
            p:not(:first-child){
                grid-column: 2 / 3;
            }
        }
    }

    /* Specific classes to affect tables within HTML blocks */
    &.table-no-border{
        > div{
            table{
                border-color: transparent;
                td, th {
                    border-color: transparent;
                }
            }
        }
    }
    &.table-first-column-nowrap{
        > div{
            table{
                td:first-child{
                    white-space: nowrap;
                    vertical-align: baseline;
                }
            }
        }
    }
    &.table-second-column-nowrap{
        > div{
            table{
                td:nth-child(2){
                    white-space: nowrap;
                    vertical-align: baseline;
                }
            }
        }
    }
    &.table-third-column-nowrap{
        > div{
            table{
                td:nth-child(3){
                    white-space: nowrap;
                    vertical-align: baseline;
                }
            }
        }
    }
    &.table-fourth-column-nowrap{
        > div{
            table{
                td:nth-child(4){
                    white-space: nowrap;
                    vertical-align: baseline;
                }
            }
        }
    }
    &.coin-order-review{
        > div{
            table{
                border: 0;
                td, th{
                    border-bottom: 0;
                    border-right: 0;
                }
                tr{
                    td:nth-child(2){
                        font-size: ${props => props.theme.fontSizes[1]};
                        color: ${props => props.theme.colors.monotoneMidDark};
                    }
                }
            }
            ${media.xs.down`
                table{
                    td{
                        font-size: ${props => props.theme.fontSizes[1]};
                    }
                    th{
                        font-size: ${props => props.theme.fontSizes[0]};
                    }
                    tr{
                        td:nth-child(2){
                            font-size: ${props => props.theme.fontSizes[0]};
                        }
                    }
                }
            `}
        }
    }
        /* Button styled links in HTML block */
    &.u-button-primary, &.u-button-secondary{
        > div a {
            border-radius: ${props => props.theme.radius};
            font-size: ${remCalc(16)};
            font-weight: ${props => props.theme.fontWeights.semiBold};
            display: flex;
            margin-right: 0;
            padding-left: ${props => props.theme.space[4]};
            padding-right: ${props => props.theme.space[4]};
            height: ${props => props.theme.space[15]};
            text-decoration: none;
            align-items: center;
            justify-content: center;
            border: ${remCalc(1)} solid;
        }
    }
    &.u-button-primary{
        > div a {
            background: ${props => props.theme.colors.primary};
            border-color: ${props => props.theme.colors.primary};
            color: ${props => props.theme.colors.brilliant};
            &[target="_blank"]:after {
                    background-image: url("data:image/svg+xml,%3C?xml version='1.0' encoding='UTF-8'?%3E %3Csvg  viewBox='0 0 16 16' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E %3Ctitle%3Ee%3C/title%3E %3Cg id='Templates' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E %3Cpath d='M5.0824769,12.0015612 C5.16844471,12.0015612 5.28743246,11.9521533 5.34810126,11.8912404 L13.8543371,3.38469938 L15.3608948,4.89095189 C15.421411,4.95097983 15.5397274,4.99968577 15.6249627,4.99968577 C15.8317799,4.99968577 15.9997787,4.83183949 15.9999619,4.62502235 L15.9999619,0.375032485 C15.9999619,0.168032246 15.831963,3.33786011e-05 15.6249627,3.33786011e-05 L11.3749729,3.33786011e-05 C11.1681557,0.000216483633 11.0003095,0.168215351 11.0003095,0.375032485 C11.0003095,0.460695122 11.0494427,0.579408218 11.1099589,0.640046501 L12.6159062,2.14660419 L4.10967038,10.6528095 C4.04878795,10.7134783 3.99938011,10.8324966 3.99938011,10.9184339 C3.99938011,11.0044017 4.04878795,11.1233894 4.10967038,11.1840582 L4.81685253,11.8912404 C4.87752133,11.9521533 4.9965396,12.0015612 5.0824769,12.0015612 Z M12.4999702,15.9999952 C13.3279712,15.9999952 13.9999666,15.3279998 13.9999666,14.4999988 L13.9999666,9.50001073 C13.9999666,9.22401041 13.7759681,9.00001192 13.4999678,9.00001192 L12.999969,9.00001192 C12.7239687,9.00001192 12.4999702,9.22401041 12.4999702,9.50001073 L12.4999702,14.3124993 C12.4999702,14.4159841 12.4159555,14.4999988 12.3124706,14.4999988 L1.68749598,14.4999988 C1.58401112,14.4999988 1.5,14.4159841 1.5,14.3124993 L1.5,3.68752459 C1.5,3.58403973 1.58401112,3.50002861 1.68749598,3.50002861 L6.4999845,3.50002861 C6.77598482,3.50002861 6.99998331,3.27602654 6.99998331,3.00002861 L6.99998331,2.50002861 C6.99998331,2.2240271 6.77598482,2.00002861 6.4999845,2.00002861 L1.5,2.00002861 C0.671995468,2.00002861 0,2.67202408 0,3.50002861 L0,14.4999988 C0,15.3279998 0.671995468,15.9999952 1.5,15.9999952 L12.4999702,15.9999952 Z' id='e' fill='%23FFFFFF' fill-rule='nonzero'%3E%3C/path%3E %3C/g%3E %3C/svg%3E");
                }
            &:hover{
                border-color: ${props => props.theme.colors.primaryTone};
                background: ${props => props.theme.colors.primaryTone};
                color: ${props => props.theme.colors.brilliant};
                &[target="_blank"]:after {
                    background-image: url("data:image/svg+xml,%3C?xml version='1.0' encoding='UTF-8'?%3E %3Csvg  viewBox='0 0 16 16' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E %3Ctitle%3Ee%3C/title%3E %3Cg id='Templates' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E %3Cpath d='M5.0824769,12.0015612 C5.16844471,12.0015612 5.28743246,11.9521533 5.34810126,11.8912404 L13.8543371,3.38469938 L15.3608948,4.89095189 C15.421411,4.95097983 15.5397274,4.99968577 15.6249627,4.99968577 C15.8317799,4.99968577 15.9997787,4.83183949 15.9999619,4.62502235 L15.9999619,0.375032485 C15.9999619,0.168032246 15.831963,3.33786011e-05 15.6249627,3.33786011e-05 L11.3749729,3.33786011e-05 C11.1681557,0.000216483633 11.0003095,0.168215351 11.0003095,0.375032485 C11.0003095,0.460695122 11.0494427,0.579408218 11.1099589,0.640046501 L12.6159062,2.14660419 L4.10967038,10.6528095 C4.04878795,10.7134783 3.99938011,10.8324966 3.99938011,10.9184339 C3.99938011,11.0044017 4.04878795,11.1233894 4.10967038,11.1840582 L4.81685253,11.8912404 C4.87752133,11.9521533 4.9965396,12.0015612 5.0824769,12.0015612 Z M12.4999702,15.9999952 C13.3279712,15.9999952 13.9999666,15.3279998 13.9999666,14.4999988 L13.9999666,9.50001073 C13.9999666,9.22401041 13.7759681,9.00001192 13.4999678,9.00001192 L12.999969,9.00001192 C12.7239687,9.00001192 12.4999702,9.22401041 12.4999702,9.50001073 L12.4999702,14.3124993 C12.4999702,14.4159841 12.4159555,14.4999988 12.3124706,14.4999988 L1.68749598,14.4999988 C1.58401112,14.4999988 1.5,14.4159841 1.5,14.3124993 L1.5,3.68752459 C1.5,3.58403973 1.58401112,3.50002861 1.68749598,3.50002861 L6.4999845,3.50002861 C6.77598482,3.50002861 6.99998331,3.27602654 6.99998331,3.00002861 L6.99998331,2.50002861 C6.99998331,2.2240271 6.77598482,2.00002861 6.4999845,2.00002861 L1.5,2.00002861 C0.671995468,2.00002861 0,2.67202408 0,3.50002861 L0,14.4999988 C0,15.3279998 0.671995468,15.9999952 1.5,15.9999952 L12.4999702,15.9999952 Z' id='e' fill='%23FFFFFF' fill-rule='nonzero'%3E%3C/path%3E %3C/g%3E %3C/svg%3E");
                }
            }
        }
    }
    &.u-button-secondary{
        > div a {
            border-color: ${props => props.theme.colors.primary};
            color: ${props => props.theme.colors.primary};
            &:hover{
                border-color: ${props => props.theme.colors.primary};
                background: ${props => props.theme.colors.primary};
                color: ${props => props.theme.colors.brilliant};
                &[target="_blank"]:after {
                    background-image: url("data:image/svg+xml,%3C?xml version='1.0' encoding='UTF-8'?%3E %3Csvg  viewBox='0 0 16 16' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E %3Ctitle%3Ee%3C/title%3E %3Cg id='Templates' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E %3Cpath d='M5.0824769,12.0015612 C5.16844471,12.0015612 5.28743246,11.9521533 5.34810126,11.8912404 L13.8543371,3.38469938 L15.3608948,4.89095189 C15.421411,4.95097983 15.5397274,4.99968577 15.6249627,4.99968577 C15.8317799,4.99968577 15.9997787,4.83183949 15.9999619,4.62502235 L15.9999619,0.375032485 C15.9999619,0.168032246 15.831963,3.33786011e-05 15.6249627,3.33786011e-05 L11.3749729,3.33786011e-05 C11.1681557,0.000216483633 11.0003095,0.168215351 11.0003095,0.375032485 C11.0003095,0.460695122 11.0494427,0.579408218 11.1099589,0.640046501 L12.6159062,2.14660419 L4.10967038,10.6528095 C4.04878795,10.7134783 3.99938011,10.8324966 3.99938011,10.9184339 C3.99938011,11.0044017 4.04878795,11.1233894 4.10967038,11.1840582 L4.81685253,11.8912404 C4.87752133,11.9521533 4.9965396,12.0015612 5.0824769,12.0015612 Z M12.4999702,15.9999952 C13.3279712,15.9999952 13.9999666,15.3279998 13.9999666,14.4999988 L13.9999666,9.50001073 C13.9999666,9.22401041 13.7759681,9.00001192 13.4999678,9.00001192 L12.999969,9.00001192 C12.7239687,9.00001192 12.4999702,9.22401041 12.4999702,9.50001073 L12.4999702,14.3124993 C12.4999702,14.4159841 12.4159555,14.4999988 12.3124706,14.4999988 L1.68749598,14.4999988 C1.58401112,14.4999988 1.5,14.4159841 1.5,14.3124993 L1.5,3.68752459 C1.5,3.58403973 1.58401112,3.50002861 1.68749598,3.50002861 L6.4999845,3.50002861 C6.77598482,3.50002861 6.99998331,3.27602654 6.99998331,3.00002861 L6.99998331,2.50002861 C6.99998331,2.2240271 6.77598482,2.00002861 6.4999845,2.00002861 L1.5,2.00002861 C0.671995468,2.00002861 0,2.67202408 0,3.50002861 L0,14.4999988 C0,15.3279998 0.671995468,15.9999952 1.5,15.9999952 L12.4999702,15.9999952 Z' id='e' fill='%23FFFFFF' fill-rule='nonzero'%3E%3C/path%3E %3C/g%3E %3C/svg%3E");
                }
            }

        }
    }
    &.warning-text{
        > div {
            background: ${props => props.theme.colors.brilliant};
            border: ${remCalc(2)} solid ${props =>
    props.theme.colors.monotoneMidDark};
            padding: ${props => props.theme.space[4]};
            * {
                font-size: ${remCalc(16)};
                color: ${props => props.theme.colors.monotoneDark};
                font-weight: ${props => props.theme.fontWeights.bold};
                &:last-child{
                    padding-bottom: 0;
                    margin-bottom: 0;
                }
            }
        }
    }

    /* Responsive colours for text within HTML blocks */
    &.u-color-primary{
        > div *:not(a) {
            color: ${props => props.theme.colors.primary} !important;
        }
    }
    &.u-color-primaryTone{
        > div *:not(a) {
            color: ${props => props.theme.colors.primaryTone} !important;
        }
    }
    &.u-color-secondary{
        > div *:not(a) {
            color: ${props => props.theme.colors.secondary} !important;
        }
    }
    &.u-color-secondaryTone{
        > div *:not(a) {
            color: ${props => props.theme.colors.secondaryTone} !important;
        }
    }
    &.u-color-tertiary{
        > div *:not(a) {
            color: ${props => props.theme.colors.tertiary} !important;
        }
    }
    &.u-color-monotoneMidDark{
        > div *:not(a) {
            color: ${props => props.theme.colors.monotoneMidDark} !important;
        }
    }
    &.u-color-monotoneDark{
        > div *:not(a) {
            color: ${props => props.theme.colors.monotoneDark} !important;
        }
    }
    &.u-color-alert{
        > div *:not(a) {
            color: ${props => props.theme.colors.alert} !important;
        }
    }
    &.u-color-accentPrimary{
        > div *:not(a) {
            color: ${props => props.theme.colors.accentPrimary} !important;
        }
    }
    &.u-color-accentSecondary{
        > div *:not(a) {
            color: ${props => props.theme.colors.accentSecondary} !important;
        }
    }
    &.u-color-accent3{
        > div *:not(a) {
            color: ${props => props.theme.colors.accent3} !important;
        }
    }
    &.u-color-accent4{
        > div *:not(a) {
            color: ${props => props.theme.colors.accent4} !important;
        }
    }
    &.u-color-brilliant{
        > div *:not(a) {
            color: ${props => props.theme.colors.brilliant} !important;
        }
    }

    ${media.sm.only`
        &.u-color-sm-primary{
            > div *:not(a) {
                color: ${props => props.theme.colors.primary} !important;
            }
        }
        &.u-color-sm-primaryTone{
            > div *:not(a) {
                color: ${props => props.theme.colors.primaryTone} !important;
            }
        }
        &.u-color-sm-secondary{
            > div *:not(a) {
                color: ${props => props.theme.colors.secondary} !important;
            }
        }
        &.u-color-sm-secondaryTone{
            > div *:not(a) {
                color: ${props => props.theme.colors.secondaryTone} !important;
            }
        }
        &.u-color-sm-tertiary{
            > div *:not(a) {
                color: ${props => props.theme.colors.tertiary} !important;
            }
        }
        &.u-color-sm-monotoneMidDark{
            > div *:not(a) {
                color: ${props =>
                    props.theme.colors.monotoneMidDark} !important;
            }
        }
        &.u-color-sm-monotoneDark{
            > div *:not(a) {
                color: ${props => props.theme.colors.monotoneDark} !important;
            }
        }
        &.u-color-sm-alert{
            > div *:not(a) {
                color: ${props => props.theme.colors.alert} !important;
            }
        }
        &.u-color-sm-accentPrimary{
            > div *:not(a) {
                color: ${props => props.theme.colors.accentPrimary} !important;
            }
        }
        &.u-color-sm-accentSecondary{
            > div *:not(a) {
                color: ${props =>
                    props.theme.colors.accentSecondary} !important;
            }
        }
        &.u-color-sm-accent3{
            > div *:not(a) {
                color: ${props => props.theme.colors.accent3} !important;
            }
        }
        &.u-color-sm-accent4{
            > div *:not(a) {
                color: ${props => props.theme.colors.accent4} !important;
            }
        }
        &.u-color-sm-brilliant{
            > div *:not(a) {
                color: ${props => props.theme.colors.brilliant} !important;
            }
        }
    `}
    ${media.md.only`
        &.u-color-md-primary{
            > div *:not(a) {
                color: ${props => props.theme.colors.primary} !important;
            }
        }
        &.u-color-md-primaryTone{
            > div *:not(a) {
                color: ${props => props.theme.colors.primaryTone} !important;
            }
        }
        &.u-color-md-secondary{
            > div *:not(a) {
                color: ${props => props.theme.colors.secondary} !important;
            }
        }
        &.u-color-md-secondaryTone{
            > div *:not(a) {
                color: ${props => props.theme.colors.secondaryTone} !important;
            }
        }
        &.u-color-md-tertiary{
            > div *:not(a) {
                color: ${props => props.theme.colors.tertiary} !important;
            }
        }
        &.u-color-md-monotoneMidDark{
            > div *:not(a) {
                color: ${props =>
                    props.theme.colors.monotoneMidDark} !important;
            }
        }
        &.u-color-md-monotoneDark{
            > div *:not(a) {
                color: ${props => props.theme.colors.monotoneDark} !important;
            }
        }
        &.u-color-md-alert{
            > div *:not(a) {
                color: ${props => props.theme.colors.alert} !important;
            }
        }
        &.u-color-md-accentPrimary{
            > div *:not(a) {
                color: ${props => props.theme.colors.accentPrimary} !important;
            }
        }
        &.u-color-md-accentSecondary{
            > div *:not(a) {
                color: ${props =>
                    props.theme.colors.accentSecondary} !important;
            }
        }
        &.u-color-md-accent3{
            > div *:not(a) {
                color: ${props => props.theme.colors.accent3} !important;
            }
        }
        &.u-color-md-accent4{
            > div *:not(a) {
                color: ${props => props.theme.colors.accent4} !important;
            }
        }
        &.u-color-md-brilliant{
            > div *:not(a) {
                color: ${props => props.theme.colors.brilliant} !important;
            }
        }
    `}
    ${media.lg.only`
        &.u-color-lg-primary{
            > div *:not(a) {
                color: ${props => props.theme.colors.primary} !important;
            }
        }
        &.u-color-lg-primaryTone{
            > div *:not(a) {
                color: ${props => props.theme.colors.primaryTone} !important;
            }
        }
        &.u-color-lg-secondary{
            > div *:not(a) {
                color: ${props => props.theme.colors.secondary} !important;
            }
        }
        &.u-color-lg-secondaryTone{
            > div *:not(a) {
                color: ${props => props.theme.colors.secondaryTone} !important;
            }
        }
        &.u-color-lg-tertiary{
            > div *:not(a) {
                color: ${props => props.theme.colors.tertiary} !important;
            }
        }
        &.u-color-lg-monotoneMidDark{
            > div *:not(a) {
                color: ${props =>
                    props.theme.colors.monotoneMidDark} !important;
            }
        }
        &.u-color-lg-monotoneDark{
            > div *:not(a) {
                color: ${props => props.theme.colors.monotoneDark} !important;
            }
        }
        &.u-color-lg-alert{
            > div *:not(a) {
                color: ${props => props.theme.colors.alert} !important;
            }
        }
        &.u-color-lg-accentPrimary{
            > div *:not(a) {
                color: ${props => props.theme.colors.accentPrimary} !important;
            }
        }
        &.u-color-lg-accentSecondary{
            > div *:not(a) {
                color: ${props =>
                    props.theme.colors.accentSecondary} !important;
            }
        }
        &.u-color-lg-accent3{
            > div *:not(a) {
                color: ${props => props.theme.colors.accent3} !important;
            }
        }
        &.u-color-lg-accent4{
            > div *:not(a) {
                color: ${props => props.theme.colors.accent4} !important;
            }
        }
        &.u-color-lg-brilliant{
            > div *:not(a) {
                color: ${props => props.theme.colors.brilliant} !important;
            }
        }
    `}
    ${media.xl.only`
        &.u-color-xl-primary{
            > div *:not(a) {
                color: ${props => props.theme.colors.primary} !important;
            }
        }
        &.u-color-xl-primaryTone{
            > div *:not(a) {
                color: ${props => props.theme.colors.primaryTone} !important;
            }
        }
        &.u-color-xl-secondary{
            > div *:not(a) {
                color: ${props => props.theme.colors.secondary} !important;
            }
        }
        &.u-color-xl-secondaryTone{
            > div *:not(a) {
                color: ${props => props.theme.colors.secondaryTone} !important;
            }
        }
        &.u-color-xl-tertiary{
            > div *:not(a) {
                color: ${props => props.theme.colors.tertiary} !important;
            }
        }
        &.u-color-xl-monotoneMidDark{
            > div *:not(a) {
                color: ${props =>
                    props.theme.colors.monotoneMidDark} !important;
            }
        }
        &.u-color-xl-monotoneDark{
            > div *:not(a) {
                color: ${props => props.theme.colors.monotoneDark} !important;
            }
        }
        &.u-color-xl-alert{
            > div *:not(a) {
                color: ${props => props.theme.colors.alert} !important;
            }
        }
        &.u-color-xl-accentPrimary{
            > div *:not(a) {
                color: ${props => props.theme.colors.accentPrimary} !important;
            }
        }
        &.u-color-xl-accentSecondary{
            > div *:not(a) {
                color: ${props =>
                    props.theme.colors.accentSecondary} !important;
            }
        }
        &.u-color-xl-accent3{
            > div *:not(a) {
                color: ${props => props.theme.colors.accent3} !important;
            }
        }
        &.u-color-xl-accent4{
            > div *:not(a) {
                color: ${props => props.theme.colors.accent4} !important;
            }
        }
        &.u-color-xl-brilliant{
            > div *:not(a) {
                color: ${props => props.theme.colors.brilliant} !important;
            }
        }
    `}
`;
StyledContainer.defaultProps = {
    theme: {
        ...defaultTheme
    },
    display: "flex",
    flexDirection: "column",
    pb: 6,
    opacity: 1
};
export { StyledContainer, LabelContainer, FieldDescription, FieldError };
